import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { mockSectors } from "../mock";
import { ArrowRight, CheckCircle } from "lucide-react";

const SectorsPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-excel-steel to-excel-dark py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t("sectors")} desservis
            </h1>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              Excel Levage accompagne une large gamme d'industries avec des solutions adaptées à chaque secteur d'activité.
            </p>
          </div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mockSectors.map((sector, index) => (
              <div key={sector.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="space-y-4">
                    <Badge className="bg-excel-light-red text-white">
                      Secteur spécialisé
                    </Badge>
                    
                    <h2 className="text-3xl md:text-4xl font-bold text-excel-dark">
                      {t(sector.name)}
                    </h2>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {t(sector.description)}
                    </p>
                  </div>

                  {/* Applications */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-excel-dark">Applications principales :</h3>
                    <div className="space-y-2">
                      {sector.applications.map((application, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <CheckCircle className="h-5 w-5 text-excel-light-red flex-shrink-0" />
                          <span className="text-gray-700">{application}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button className="bg-excel-light-red hover:bg-excel-red text-white">
                      <Link to="/contact" className="flex items-center">
                        Vérifier votre secteur
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                      <Link to="/case-studies">
                        Voir nos réalisations
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <img
                    src={sector.image}
                    alt={t(sector.name)}
                    className="w-full h-96 object-cover rounded-lg shadow-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              Notre expertise sectorielle
            </h2>
            <p className="text-xl text-gray-600">
              Chaque secteur a ses spécificités, nous les connaissons
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-l-4 border-l-excel-light-red">
              <CardHeader>
                <CardTitle className="text-excel-dark">Environnements exigeants</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-gray-600">
                  Nos équipements résistent aux conditions les plus difficiles : températures extrêmes, humidité, poussières industrielles.
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-excel-light-red">
              <CardHeader>
                <CardTitle className="text-excel-dark">Normes spécifiques</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-gray-600">
                  Conformité aux réglementations de chaque secteur : HACCP pour l'agroalimentaire, ATEX pour les zones explosives.
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-excel-light-red">
              <CardHeader>
                <CardTitle className="text-excel-dark">Solutions sur mesure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-gray-600">
                  Adaptation complète à vos processus industriels et contraintes d'espace pour une intégration parfaite.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-excel-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Votre secteur n'est pas listé ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour discuter de vos besoins spécifiques. Nous développons des solutions pour tous types d'industries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg"
              className="bg-excel-light-red hover:bg-excel-red text-white"
            >
              <Link to="/contact">
                Vérifier votre secteur
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SectorsPage;